import React, { useState, useEffect } from 'react';
import adminAuthService from '../../services/adminAuthService';

interface DatabaseStatusProps {
  onRetry?: () => void;
}

interface DatabaseHealth {
  connected: boolean;
  functionsAvailable: boolean;
  tablesAccessible: boolean;
  lastChecked: Date;
  errors: string[];
}

const DatabaseStatus: React.FC<DatabaseStatusProps> = ({ onRetry }) => {
  const [health, setHealth] = useState<DatabaseHealth>({
    connected: false,
    functionsAvailable: false,
    tablesAccessible: false,
    lastChecked: new Date(),
    errors: []
  });
  const [isChecking, setIsChecking] = useState(false);

  const checkDatabaseHealth = async () => {
    setIsChecking(true);
    const errors: string[] = [];
    let connected = false;
    let functionsAvailable = false;
    let tablesAccessible = false;

    try {
      // Test basic connection and functions
      const statsResult = await adminAuthService.getDashboardStats();
      if (statsResult.success) {
        connected = true;
        functionsAvailable = true;
        tablesAccessible = true;
      } else {
        connected = true; // Connection works but functions might not
        errors.push(statsResult.error || 'Dashboard stats function failed');
      }

      // Test entries function
      const entriesResult = await adminAuthService.getWhitelistEntries(1, 5, '');
      if (!entriesResult.success) {
        errors.push(entriesResult.error || 'Whitelist entries function failed');
      }

    } catch (error) {
      errors.push(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    setHealth({
      connected,
      functionsAvailable,
      tablesAccessible,
      lastChecked: new Date(),
      errors
    });
    setIsChecking(false);
  };

  useEffect(() => {
    checkDatabaseHealth();
  }, []);

  const handleRetry = () => {
    checkDatabaseHealth();
    onRetry?.();
  };

  const getStatusColor = (status: boolean) => {
    return status ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (status: boolean) => {
    return status ? '✅' : '❌';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Database Status</h3>
        <button
          onClick={handleRetry}
          disabled={isChecking}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {isChecking ? 'Checking...' : 'Retry'}
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Connection</span>
          <span className={getStatusColor(health.connected)}>
            {getStatusIcon(health.connected)} {health.connected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-700">Functions Available</span>
          <span className={getStatusColor(health.functionsAvailable)}>
            {getStatusIcon(health.functionsAvailable)} {health.functionsAvailable ? 'Available' : 'Missing'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-700">Tables Accessible</span>
          <span className={getStatusColor(health.tablesAccessible)}>
            {getStatusIcon(health.tablesAccessible)} {health.tablesAccessible ? 'Accessible' : 'Restricted'}
          </span>
        </div>

        <div className="text-sm text-gray-500">
          Last checked: {health.lastChecked.toLocaleTimeString()}
        </div>
      </div>

      {health.errors.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <h4 className="text-sm font-semibold text-red-800 mb-2">Issues Found:</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {health.errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {!health.functionsAvailable && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <h4 className="text-sm font-semibold text-yellow-800 mb-2">Setup Required:</h4>
          <p className="text-sm text-yellow-700 mb-2">
            Database functions are missing. Please run the setup script:
          </p>
          <code className="text-xs bg-gray-100 p-2 rounded block">
            node setup-admin-database.js
          </code>
          <p className="text-xs text-yellow-600 mt-2">
            Or manually execute the SQL in <code>fix-database-functions.sql</code> in Supabase SQL Editor.
          </p>
        </div>
      )}
    </div>
  );
};

export default DatabaseStatus;